// import createSentryMiddleware from "redux-sentry-middleware";
// import * as Sentry from '@sentry/browser';
import {
    Action,
    applyMiddleware,
    compose,
    createStore,
    Middleware,
    Store,
    Unsubscribe,
} from 'redux';
import { createEpicMiddleware } from 'redux-observable';
import { GameItemConfig, ItemIDs } from '../../Config/Items';
import { MapList } from '../../Config/Levels';
import { MapChestsStates } from '../../Config/MapChests';
import SoundManager from '../../Sound/SoundManager';
import { Actions, TAction } from '../Actions/Actions';
import epics from '../epics/index';
import { defaultStateBoard, IStateBoard } from '../reducers/board';
import { defaultBoosters, IStateBoosters } from '../reducers/boosters';
import { defaultCurrency, initHearts, IStateCurrency } from '../reducers/currency';
import reducers from '../reducers/index';
import { defaultStateLevel, getLevelIndex, IStateLevel } from '../reducers/level';
import { defaultMissions, IDailyMission, IStateMissions, MissionState } from '../reducers/missions';
import { defaultStatePause, IStatePause } from '../reducers/pause';
import { defaultStatePlayer, IStatePlayer } from '../reducers/player';
import { IStateScore } from '../reducers/score';
import { defaultStateSound, IStateSound } from '../reducers/sound';
import { defaultStateTracking, IStateTracking } from '../reducers/tracking';
import { defaultStateTutorial, IStateTutorial } from '../reducers/tutorial';
import { Views } from '../reducers/view';
import { deltaActions } from './Middleware/DeltaTrackingMiddleware';
import { saveActions } from './Middleware/SaveDataMiddleware';
import { sentryActionLogger } from './Middleware/SentryMiddleware';
import { defaultStateSocial, IStateSocial } from '../socialHelpers';
import { defaultScore } from '../reducers/scoreHelpers';

export interface IGameState {
    StateBoard: IStateBoard;
    StateBoosters: IStateBoosters;
    StateCurrency: IStateCurrency;
    StateLevel: IStateLevel;
    StateMissions: IStateMissions;
    StatePause: IStatePause;
    StatePlayer: IStatePlayer;
    StateScore: IStateScore;
    StateSocial: IStateSocial;
    StateSound: IStateSound;
    StateTracking: IStateTracking;
    StateTutorial: IStateTutorial;
    StateView: string;
}

const defaultGameState: IGameState = {
    StateBoard: defaultStateBoard,
    StateBoosters: defaultBoosters,
    StateCurrency: defaultCurrency,
    StateLevel: defaultStateLevel,
    StateMissions: defaultMissions,
    StatePause: defaultStatePause,
    StatePlayer: defaultStatePlayer,
    StateScore: defaultScore,
    StateSocial: defaultStateSocial,
    StateSound: defaultStateSound,
    StateTracking: defaultStateTracking,
    StateTutorial: defaultStateTutorial,
    StateView: Views.BOOT,
};

export interface IStateSubcriber<T> {
    id: string;

    selector(state: IGameState): T;

    callback(state: T, prev: T): void;
}

export interface IWatcher<T> {
    selector(state: IGameState): T;

    callback(state: T, prev: T): void;
}

export default class GameStore {
    private store: Store;
    // @ts-ignore
    private unsubscribe: Unsubscribe;
    private previousState: IGameState;
    private selectors: Array<IStateSubcriber<Partial<IGameState>>> = [];
    private watchers: Array<IWatcher<Partial<IGameState>>> = [];
    private initActions: Actions[];

    constructor(data?: any) {
        this.initActions = [];
        this.store = createStore(
            reducers,
            hydrateState.bind(this)(data),
            composeEnhancers(applyMiddleware(...middlewares))
        );

        epicMiddleWare.run(epics);

        this.unsubscribe = this.store.subscribe(this.handleStateChange.bind(this));

        this.previousState = this.getPreviousState();
        this.initActions.forEach(x => this.dispatch(x));
    }

    public getStore() {
        return this.store;
    }

    public getState(): IGameState {
        return this.store.getState();
    }

    public getPreviousState() {
        return this.previousState || this.store.getState();
    }

    public dispatch(action: Action) {
        this.previousState = this.getState();
        this.store.dispatch(action);
    }

    public addListener<T>(selectors: Array<IStateSubcriber<T>>) {
        this.selectors = [...this.selectors, ...selectors];
    }

    public addWatcher<T>(...watchers: Array<IWatcher<T>>) {
        this.watchers = [...this.watchers, ...watchers];
    }

    public removeListener<T>(selectors: Array<IStateSubcriber<T>>) {
        selectors.forEach(selector => {
            // find matching selector by id
            const index = this.selectors.indexOf(this.selectors.find(s => s.id === selector.id));
            if (index >= 0) {
                this.selectors.splice(index, 1);
            }
        });
    }

    public removeWatcher<T>(...watchers: Array<IWatcher<T>>) {
        for (const watcher of watchers) {
            this.watchers.splice(this.watchers.indexOf(watcher), 1);
        }
    }

    private handleStateChange() {
        const n = this.getState();
        const pre = this.getPreviousState();
        this.selectors.forEach(listener => {
            listener.callback(listener.selector(n), listener.selector(pre));
        });

        this.watchers.forEach(watcher => {
            const current = watcher.selector(n);
            const previous = watcher.selector(pre);
            if (current !== previous) {
                watcher.callback(current, previous);
            }
        });

        this.previousState = this.getState();
    }
}

function hydrateState(data?: any) {
    const preloadState = defaultGameState;
    return {
        ...preloadState,
        StateBoosters: restoreBoosters.bind(this)(preloadState.StateBoosters, data.boosters),
        StateCurrency: restoreCurrency(preloadState.StateCurrency, data),
        StatePlayer: restorePlayer(preloadState.StatePlayer, data),
        StateSound: restoreSound(preloadState.StateSound, data),
        StateSocial: restoreSocial(preloadState.StateSocial, data),
        StateTracking: restoreTracking(preloadState.StateTracking, data),
        StateMissions: restoreMissions(preloadState.StateMissions, data),
    };
}

function restoreBoosters(boosters: IStateBoosters, data: any) {
    const restoredBoosters = !data
        ? boosters.availableBoosters
        : data.map((d: string) => d.charAt(0).toUpperCase() + d.substr(1));

    const hintNumber = restoredBoosters.filter((x: string) => x === ItemIDs.HINT).length;
    if (hintNumber > 0) {
        const hintPrice = GameItemConfig[ItemIDs.HINT].price;
        const refund = hintNumber * hintPrice;
        this.initActions.push(Actions.receiveGift([ItemIDs.COINS, refund]));
        for (let i = 0; i < hintNumber; i++) {
            this.initActions.push(Actions.removeStoredBooster(ItemIDs.HINT));
        }
    }

    return {
        ...boosters,
        availableBoosters: restoredBoosters,
    };
}

function restoreCurrency(currency: IStateCurrency, data: any) {
    let startHearts;
    if (parseInt(data.heart, 10) || parseInt(data.heart, 10) === 0) {
        startHearts = parseInt(data.heart, 10);
    } else {
        startHearts = currency.heart;
    }

    const heartCurrencyData = data.lastLifeTime
        ? initHearts(startHearts, data.lastLifeTime)
        : [startHearts, 0];

    const coinData =
        parseInt(data.coin, 10) || parseInt(data.coin, 10) === 0
            ? parseInt(data.coin, 10)
            : currency.coin;

    return {
        ...currency,
        coin: coinData,
        heart: heartCurrencyData[0],
        ts: heartCurrencyData[1],
    };
}

function restorePlayer(player: IStatePlayer, data: any) {
    // filter unlocked maps to handle players that may have unlocked 'null'
    const unlockedData = data.unlockedMaps
        ? (data.unlockedMaps as string[]).filter(Boolean)
        : player.unlocked;

    const unlockedChestsData = data.unlockedChests
        ? (data.unlockedChests as MapChestsStates[]).filter(Boolean)
        : player.unlockedChests;

    const restoreId =
        unlockedData && unlockedData.length > 0 ? unlockedData[unlockedData.length - 1] : 'machu';

    let restoreLast = data.lastLevelIndex
        ? parseInt(data.lastLevelIndex, 10)
        : player.lastLevelIndex;

    const purchases = data.purchases || player.purchases;

    const opened = data.shopHasBeenOpened || false;
    /*if (data.played && Object.keys(data.played).length > 0) {
        restoreLast = getLevelIndex(Object.keys(data.played).pop());

    }
*/

    if (data.played && Object.keys(data.played).length > 0) {
        let greatestMap = -1;
        let greatestLevel = -1;
        let greatestId = '';
        Object.keys(data.played).forEach((a: string) => {
            const [map, level] = a.split('-');
            const currentMapIndex = MapList.indexOf(map);
            const currentLevelNumber = parseInt(level, 10);
            if (
                currentMapIndex > greatestMap ||
                (currentMapIndex === greatestMap && currentLevelNumber > greatestLevel)
            ) {
                greatestMap = currentMapIndex;
                greatestLevel = currentLevelNumber;
                greatestId = a;
            }
        });
        restoreLast = getLevelIndex(greatestId);
    }

    return {
        ...player,
        activeCollection: data.activeCollection || player.activeCollection,
        collectablesProgress: data.collectablesProgress || player.collectablesProgress,
        lastInterstitialDate: data.lastInterstitialDate || player.lastInterstitialDate,
        levelId: data.levelId || player.levelId,
        mapId: restoreId,
        prizeWheelCooldown: data.prizeWheelCooldown || player.prizeWheelCooldown,
        mapLevels: data.played || {},
        totalStars: parseInt(data.totalStars, 10) || player.totalStars,
        optInShortcutRewardDate: data.optInShortcutRewardDate,
        unlocked: unlockedData,
        unlockedChests: unlockedChestsData,
        visited: data.visitedMaps || player.visited,
        purchases,
        lastLevelIndex: restoreLast,
        shopHasBeenOpened: opened,
        shopGift: data.shopGift || player.shopGift,
    };
}

function restoreSound(sound: IStateSound, data: any) {
    const soundFx = data.isSoundOn == null || data.isSoundOn === 'true';
    const music = data.isMusicOn == null || data.isMusicOn === 'true';
    SoundManager.setSoundEabled(soundFx);
    SoundManager.setMusicEnabled(music);
    return {
        ...sound,
        soundFx,
        music,
    };
}

function restoreSocial(social: IStateSocial, data: any) {
    return {
        ...social,
        messageIds: data.messageIds || social.messageIds,
        optInRewardDate: data.optInRewardDate || social.optInRewardDate,
        confirmedInvitations: data.confirmedInvites || social.confirmedInvitations,
        receivedInvitations: data.receivedInvites || social.receivedInvitations,
        pendingInvitations: data.pendingInvitations || social.pendingInvitations,
        sharedGiftsCount: data.sharedGiftsCount || social.sharedGiftsCount,
        sentCoins: data.sentCoins || social.sentCoins,
        sentGifts: data.sentGifts || social.sentGifts,
        requestedGifts: data.requestedGifts || social.requestedGifts,
    };
}

function restoreTracking(tracking: IStateTracking, data: any) {
    return {
        ...tracking,
        tracked: data.tracked || tracking.tracked,
    };
}

function restoreMissions(missionsDefault: IStateMissions, data: any) {
    const missions: IDailyMission[] = data.missions
        ? Object.values(data.missions)
        : missionsDefault.missions;

    let completedCount = parseInt(data.missionsCompleted, 10) || missionsDefault.completedCount;
    if (completedCount > 10) {
        const restoreNum = missions.filter(m => m.missionState === MissionState.COMPLETE).length;
        completedCount = 5 + restoreNum;
    }

    missions.forEach(x => {
        const itemIds = Object.values(ItemIDs);
        const index = itemIds.findIndex(y => y.toLowerCase() === x.targetId);
        if (index !== -1) {
            x.targetId = itemIds[index];
        }
    });
    return {
        ...missionsDefault,
        missions,
        refreshTimer: parseInt(data.missionRefreshTimer, 10) || missionsDefault.refreshTimer,
        completedCount,
        missionsShown: data.missionsShown === 'true',
        claimedRewards: data.claimedRewards
            ? (data.claimedRewards as boolean[])
            : missionsDefault.claimedRewards,
    };
}

const epicMiddleWare = createEpicMiddleware<TAction, TAction, IGameState>();
const middlewares: Middleware[] = [epicMiddleWare];
middlewares.push(saveActions);
middlewares.push(deltaActions);
if (sgPlatform === 'fbinstant') {
    middlewares.push(sentryActionLogger);
}

const composeEnhancers =
    !process || !DEBUG ? compose : window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
